import React from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { UnitSystem } from 'providers/api';
import { i18n } from 'i18n';
import { distanceMetricToImperial, volumeMetricToImperial, diameterMetricToImperial } from 'utils/unitConversions';
import { RouteVolume } from '../../../MineModel/Route/volumeUtils';
import { Model } from '../../../hooks/useModelState';
import { TOOLBAR_MENU_Z_INDEX } from './utils';

type DisplayUnit = 'metric' | 'imperial';

interface RouteVolumePopoutProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  model: Model;
  routeVolume: RouteVolume | null;
}

const formatLength = (metres: number, unit: DisplayUnit): string => {
  const value = unit === 'imperial' ? distanceMetricToImperial(metres) : metres;
  return `${value.toFixed(1)} ${unit === 'imperial' ? 'ft' : 'm'}`;
};

const formatVolume = (cubicMetres: number, unit: DisplayUnit): string => {
  const value = unit === 'imperial' ? volumeMetricToImperial(cubicMetres) : cubicMetres;
  return `${value.toFixed(2)} ${unit === 'imperial' ? 'ft³' : 'm³'}`;
};

const formatDiameter = (metres: number, unit: DisplayUnit): string => {
  if (metres <= 0) return i18n._('no size');
  const millimetres = metres * 1000;
  const value = unit === 'imperial' ? diameterMetricToImperial(millimetres) : millimetres;
  return `⌀ ${value.toFixed(unit === 'imperial' ? 1 : 0)} ${unit === 'imperial' ? 'in' : 'mm'}`;
};

const RouteVolumePopout = ({
  open,
  anchorEl,
  onClose,
  model,
  routeVolume,
}: RouteVolumePopoutProps) => {
  const displayUnit: DisplayUnit = model.project?.unitSystemPreference === UnitSystem.Imperial ? 'imperial' : 'metric';

  const buildLines = (volume: RouteVolume): string[] => [
    ...volume.breakdown.map(({ pipeName, insideDiameter, lengthMetres, volumeCubicMetres }) => [
      pipeName,
      formatDiameter(insideDiameter, displayUnit),
      formatLength(lengthMetres, displayUnit),
      formatVolume(volumeCubicMetres, displayUnit),
    ].join('\t')),
    ['Total', '', formatLength(volume.totalLengthMetres, displayUnit), formatVolume(volume.totalVolumeCubicMetres, displayUnit)].join('\t'),
  ];

  const handleCopyAll = () => {
    if (routeVolume) navigator.clipboard.writeText(buildLines(routeVolume).join('\n'));
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ zIndex: TOOLBAR_MENU_Z_INDEX }}
    >
      <Box sx={{ p: 1.5, minWidth: 320, maxWidth: 420 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{i18n._('Route Volume')}</Typography>
          {routeVolume && (
            <Tooltip title={i18n._('Copy to clipboard')}>
              <Button
                size="small"
                color="secondary"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={handleCopyAll}
                sx={{ textTransform: 'none' }}
              >
                {i18n._('Copy all')}
              </Button>
            </Tooltip>
          )}
        </Box>
        <Divider />
        {!routeVolume ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {i18n._('Click two nodes in the model to measure a route volume.')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
            {routeVolume.breakdown.map(({ pipeId, pipeName, pipeColour, insideDiameter, lengthMetres, volumeCubicMetres }) => (
              <Box
                key={pipeId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '2px',
                    backgroundColor: pipeColour,
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {pipeName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${formatDiameter(insideDiameter, displayUnit)} · ${formatLength(lengthMetres, displayUnit)}`}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {formatVolume(volumeCubicMetres, displayUnit)}
                </Typography>
              </Box>
            ))}
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {i18n._('Total')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: 'right' }}>
                {formatLength(routeVolume.totalLengthMetres, displayUnit)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {formatVolume(routeVolume.totalVolumeCubicMetres, displayUnit)}
              </Typography>
            </Box>
            {routeVolume.unsizedLengthMetres > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                {i18n._('{length} of the route has no pipe size and is excluded from the volume.', {
                  length: formatLength(routeVolume.unsizedLengthMetres, displayUnit),
                })}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Popover>
  );
};

export default RouteVolumePopout;
